import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { X, Crown, Check, Zap, TrendingUp, Lock, Star, BarChart } from 'lucide-react';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useAuth } from '../contexts/AuthContext';
import { useConfig } from '../contexts/ConfigContext';
import { colors, typography, spacing, radius } from '../../styles/theme';

const UpgradeModal = ({ isOpen, onClose }) => {
  const { tier, upgradeAccount, getTierBenefits } = useSubscription();
  const { isAuthenticated } = useAuth();
  const { config, getPricing } = useConfig();
  const [billingPeriod, setBillingPeriod] = useState('monthly'); // 'monthly' or 'annual'
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  // Get dynamic pricing from config
  const proMonthly = getPricing('pro', 'monthly');
  const proAnnual = getPricing('pro', 'annual');
  
  // Calculate savings
  const monthlyCost = proMonthly?.amount || 7.99;
  const annualCost = proAnnual?.amount || 49.99;
  const annualMonthlyCost = annualCost / 12;
  const savingsPercent = Math.round((1 - (annualCost / (monthlyCost * 12))) * 100);

  const handleUpgrade = async () => {
    // Check if user is logged in
    if (!isAuthenticated) {
      if (confirm('You need to be logged in to upgrade to Pro.\n\nWould you like to sign up for a free account first?')) {
        window.location.href = '/signup';
      }
      return;
    }

    setLoading(true);
    try {
      await upgradeAccount(billingPeriod);
      // Stripe will redirect to checkout
    } catch (error) {
      console.error('Upgrade error:', error);
      alert('Failed to start checkout. Please try again.');
      setLoading(false);
    }
  };

  const pricing = {
    monthly: { price: monthlyCost, per: 'month', total: monthlyCost },
    annual: { price: annualCost, per: 'year', monthly: annualMonthlyCost, savings: savingsPercent }
  };

  const benefits = [
    { icon: Zap, text: 'Unlimited rounds per day', color: '#10b981' },
    { icon: Star, text: 'All 24 dimensions unlocked', color: '#fbbf24' },
    { icon: TrendingUp, text: 'Unlimited progress history', color: '#a78bfa' },
    { icon: BarChart, text: 'Advanced analytics & insights', color: '#ec4899' },
    { icon: Crown, text: 'Premium training protocols', color: '#f59e0b' }
  ];

  if (!isOpen) return null;

  const modalContent = (
    <>
      {/* Backdrop */}
      <div 
        onClick={onClose}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(8px)',
          zIndex: 999999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}
      >
        {/* Modal */}
        <div 
          onClick={(e) => e.stopPropagation()}
          style={{
            background: colors.bgPrimary,
            borderRadius: radius.xl,
            width: '700px',
            maxWidth: '75vw',
            maxHeight: '80vh',
            overflow: 'auto',
            border: `1px solid ${colors.border}`,
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.8)',
            position: 'relative',
            margin: '80px 100px',
          }}
        >
          {/* Header */}
          <div style={{
            padding: `${spacing[8]} ${spacing[6]}`,
            borderBottom: `1px solid ${colors.borderLight}`,
            position: 'relative',
            textAlign: 'center'
          }}>
            <button
              onClick={onClose}
              disabled={loading}
              style={{
                position: 'absolute',
                top: spacing[4],
                right: spacing[4],
                background: 'rgba(15, 23, 42, 0.8)',
                border: `1px solid ${colors.border}`,
                color: colors.textPrimary,
                cursor: loading ? 'not-allowed' : 'pointer',
                padding: spacing[3],
                borderRadius: radius.full,
                transition: 'all 0.15s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 100,
                width: '40px',
                height: '40px'
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.target.style.background = colors.bgInput;
                  e.target.style.borderColor = colors.primary;
                }
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(15, 23, 42, 0.8)';
                e.target.style.borderColor = colors.border;
              }}
            >
              <X size={20} />
            </button>
            
            <Crown size={36} color={colors.warning} style={{ marginBottom: spacing[4] }} />
            <h2 style={{
              margin: 0,
              marginBottom: spacing[2],
              fontSize: typography.xl,
              fontWeight: typography.bold,
              background: `linear-gradient(135deg, ${colors.warning} 0%, #f59e0b 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              letterSpacing: '-0.01em',
              fontFamily: typography.fontSans
            }}>
              Upgrade to Pro
            </h2>
            <p style={{
              margin: 0,
              color: colors.textSecondary,
              fontSize: typography.sm,
              fontWeight: typography.normal,
              fontFamily: typography.fontSans
            }}>
              Unlock unlimited training with all advanced features
            </p>
          </div>
          
          {/* Content */}
          <div style={{
            padding: `${spacing[4]}`,
          }}>

            {/* Billing Period Toggle */}
            <div style={{
              padding: spacing[5],
              display: 'flex',
              justifyContent: 'center'
            }}>
              <div style={{
                display: 'inline-flex',
                background: colors.bgInput,
                borderRadius: radius.lg,
                padding: spacing[1],
                border: `1px solid ${colors.borderLight}`,
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
              }}>
                <button
                  onClick={() => setBillingPeriod('monthly')}
                  disabled={loading}
                  style={{
                    padding: `${spacing[2]} ${spacing[6]}`,
                    background: billingPeriod === 'monthly' 
                      ? `linear-gradient(135deg, ${colors.primary} 0%, #3b82f6 100%)` 
                      : 'transparent',
                    color: billingPeriod === 'monthly' ? '#fff' : colors.textSecondary,
                    border: 'none',
                    borderRadius: radius.md,
                    fontSize: typography.sm,
                    fontWeight: typography.semibold,
                    cursor: loading ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                    fontFamily: typography.fontSans
                  }}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setBillingPeriod('annual')}
                  disabled={loading}
                  style={{
                    padding: `${spacing[2]} ${spacing[6]}`,
                    background: billingPeriod === 'annual' 
                      ? `linear-gradient(135deg, ${colors.primary} 0%, #3b82f6 100%)` 
                      : 'transparent',
                    color: billingPeriod === 'annual' ? '#fff' : colors.textSecondary,
                    border: 'none',
                    borderRadius: radius.md,
                    fontSize: typography.sm,
                    fontWeight: typography.semibold,
                    cursor: loading ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                    position: 'relative',
                    fontFamily: typography.fontSans
                  }}
                >
                  Annual
                  {billingPeriod === 'annual' && (
                    <span style={{
                      position: 'absolute',
                      top: '-8px',
                      right: '-8px',
                      background: '#10b981',
                      color: '#fff',
                      fontSize: typography.xs,
                      fontWeight: typography.bold,
                      padding: `${spacing[1]} ${spacing[2]}`,
                      borderRadius: radius.md,
                      letterSpacing: '0.025em',
                      fontFamily: typography.fontSans
                    }}>
                      Save {savingsPercent}%
                    </span>
                  )}
                </button>
              </div>
            </div>

            {/* Pricing Display */}
            <div style={{
              padding: `0 ${spacing[5]} ${spacing[4]}`,
              textAlign: 'center'
            }}>
              <div style={{
                fontSize: typography['3xl'],
                fontWeight: typography.bold,
                color: colors.textPrimary,
                marginBottom: spacing[2],
                letterSpacing: '-0.02em',
                lineHeight: typography.tight,
                fontFamily: typography.fontSans
              }}>
                ${pricing[billingPeriod].price.toFixed(2)}
                <span style={{ 
                  fontSize: typography.base, 
                  color: colors.textSecondary, 
                  fontWeight: typography.semibold
                }}>
                  /{billingPeriod === 'monthly' ? 'mo' : 'yr'}
                </span>
              </div>
              {billingPeriod === 'annual' && (
                <div style={{
                  fontSize: typography.sm,
                  color: '#10b981',
                  fontWeight: typography.semibold,
                  fontFamily: typography.fontSans
                }}>
                  That's only ${pricing.annual.monthly.toFixed(2)}/month - save ${(monthlyCost * 12 - annualCost).toFixed(0)}/year!
                </div>
              )}
            </div>

            {/* Benefits List */}
            <div style={{
              padding: spacing[5],
              background: colors.bgInput,
              margin: `0 ${spacing[5]}`,
              borderRadius: radius.lg,
              border: `1px solid ${colors.borderLight}`,
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
            }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: spacing[4],
                columnGap: spacing[5]
              }}>
                {benefits.map((benefit, idx) => (
                  <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: spacing[3] }}>
                    <benefit.icon size={18} color={benefit.color} style={{ flexShrink: 0 }} />
                    <span style={{ 
                      color: colors.textPrimary, 
                      fontSize: typography.sm,
                      fontWeight: typography.normal,
                      lineHeight: typography.snug,
                      fontFamily: typography.fontSans
                    }}>
                      {benefit.text}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA Button */}
            <div style={{ padding: spacing[5] }}>
              <button
                onClick={handleUpgrade}
                disabled={loading}
                style={{
                  width: '100%',
                  padding: spacing[4],
                  background: loading 
                    ? colors.bgInput
                    : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  border: 'none',
                  borderRadius: radius.lg,
                  color: '#fff',
                  fontSize: typography.base,
                  fontWeight: typography.bold,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: spacing[2],
                  fontFamily: typography.fontSans,
                  boxShadow: '0 4px 16px rgba(16, 185, 129, 0.25)',
                  letterSpacing: '-0.01em'
                }}
                onMouseEnter={(e) => {
                  if (!loading) {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 20px rgba(16, 185, 129, 0.35)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 16px rgba(16, 185, 129, 0.25)';
                }}
              >
                {loading ? 'Opening Checkout...' : `Start ${billingPeriod === 'monthly' ? 'Monthly' : 'Annual'} Plan`}
              </button>
              
              <p style={{
                margin: `${spacing[3]} 0 0 0`,
                fontSize: typography.xs,
                color: colors.textTertiary,
                textAlign: 'center',
                fontWeight: typography.normal,
                fontFamily: typography.fontSans
              }}>
                Cancel anytime. No questions asked.
              </p>
            </div>

          </div>
        </div>
      </div>
    </>
  );

  // Render to document body using portal
  return ReactDOM.createPortal(modalContent, document.body);
};

export default UpgradeModal;

